// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/GraphCitationDialog.test.tsx
import { Warning } from "@phosphor-icons/react";
import { Dialog, Flex, Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  type TicketMessageCitation,
  hasConflictingVerdicts,
  isGraphCitation,
} from "../utils/commentSourceUtils";

/**
 * Why the graph surfaced a claim: the subgraph behind the conclusion.
 *
 * A rule citation answers "which rule backs this". This answers "these rules
 * relate, and here is how" — the conflict, the substantiation requirement, the
 * conversation that already covered it. That reason is the graph's whole
 * contribution and a rule chip structurally cannot carry it.
 *
 * Every edge carries its own `evidence` from the builder that drew it, so each
 * line explains itself without a second lookup.
 */

/** How each relation reads to a person. The graph's own kinds are engineering
 * words; a compliance reviewer should not have to learn them. */
const RELATION_LABEL: Record<string, string> = {
  similar_to: "Same claim as",
  requires: "Requires",
  cited: "Discussed in",
  asked_about: "You were shown",
  co_cited: "Came up alongside",
  mentions: "Noted in memory",
  scoped_to: "Scoped to",
};

/** Node ids are `kind:native`. Only these carry a real row id — `brand:` and
 * `requirement:` are derived from text, so they render but never link. */
const LINKABLE = new Set(["claim", "chat", "memory", "user"]);

export function graphNodeKind(nodeId: string): string {
  return String(nodeId || "").split(":", 1)[0];
}

export function isLinkableNode(nodeId: string): boolean {
  return LINKABLE.has(graphNodeKind(nodeId));
}

export function GraphCitationDialog({
  citation,
  trigger,
}: {
  citation: TicketMessageCitation;
  trigger: ReactNode;
}) {
  const { t } = useTranslation();
  if (!isGraphCitation(citation)) return null;

  const conflicted = hasConflictingVerdicts(citation);
  const allEdges = citation.edges ?? [];

  // A requirement is a CONDITION plus the claims under it, not a list of
  // lines. Rendered flat, "requires X" and "X also governs Y" read as two
  // unrelated rows and the shared-condition insight is lost - which is the
  // whole reason a requirement node exists.
  const requirements = allEdges
    .filter(
      (edge) =>
        edge.relation === "requires" && edge.src === citation.graphNodeId,
    )
    .map((edge) => ({
      id: edge.dst,
      label: edge.dstLabel || edge.dst,
      governs: allEdges
        .filter(
          (other) =>
            other.relation === "requires" &&
            other.src === edge.dst &&
            other.dst !== citation.graphNodeId,
        )
        .map((other) => other.dstLabel || other.dst),
    }));

  // Everything the grouped view above already covers is removed here, so no
  // edge is shown twice and none is silently dropped.
  const grouped = new Set(requirements.map((r) => r.id));
  const edges = allEdges.filter(
    (edge) =>
      !(edge.relation === "requires" && grouped.has(edge.dst)) &&
      !(edge.relation === "requires" && grouped.has(edge.src)),
  );

  return (
    <Dialog.Root>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content
        maxWidth="640px"
        data-testid="graph-citation-dialog"
        aria-label={t("askPuntt.graphCitation.title")}
      >
        <Dialog.Title data-testid="graph-citation-dialog-title">
          {citation.graphLabel || t("askPuntt.graphCitation.title")}
        </Dialog.Title>
        {/* Radix warns without one, and a screen reader announces the dialog
            with no idea what it contains. */}
        <Dialog.Description size="2" color="gray" mb="3">
          {t("askPuntt.graphCitation.description")}
        </Dialog.Description>

        {conflicted ? (
          <Flex
            align="center"
            gap="2"
            mb="3"
            data-testid="graph-citation-conflict"
          >
            <Warning aria-hidden="true" />
            <Text size="2" weight="bold">
              {t("askPuntt.graphCitation.conflict", {
                verdicts: (citation.conflictingVerdicts ?? []).join(", "),
              })}
            </Text>
          </Flex>
        ) : null}

        {requirements.length > 0 ? (
          <Flex
            direction="column"
            gap="3"
            mb="3"
            data-testid="graph-citation-requirements"
          >
            {requirements.map((group, index) => (
              <Flex
                key={group.id}
                direction="column"
                gap="1"
                data-testid={`graph-citation-requirement-${index}`}
              >
                <Text size="1" color="gray">
                  {t("askPuntt.graphCitation.requires")}
                </Text>
                <Text size="2" weight="medium">
                  {group.label}
                </Text>
                {group.governs.length > 0 ? (
                  <Text
                    size="1"
                    color="gray"
                    data-testid={`graph-citation-governs-${index}`}
                  >
                    {t("askPuntt.graphCitation.governs")}
                    {": "}
                    {group.governs.join(", ")}
                  </Text>
                ) : null}
              </Flex>
            ))}
          </Flex>
        ) : null}

        {edges.length === 0 ? (
          <Text size="2" data-testid="graph-citation-empty">
            {t("askPuntt.graphCitation.empty")}
          </Text>
        ) : (
          <Flex direction="column" gap="3" data-testid="graph-citation-edges">
            {edges.map((edge, index) => {
              const relation = RELATION_LABEL[edge.relation] ?? edge.relation;
              // The far end: the node that is NOT the one this citation is
              // about, so every row reads outward from the claim in question.
              const far =
                edge.src === citation.graphNodeId ? edge.dst : edge.src;
              const farLabel =
                (edge.src === citation.graphNodeId
                  ? edge.dstLabel
                  : edge.srcLabel) || far;
              return (
                <Flex
                  key={`${edge.src}-${edge.dst}-${edge.relation}-${index}`}
                  direction="column"
                  gap="1"
                  data-testid={`graph-citation-edge-${index}`}
                >
                  <Flex align="baseline" gap="2" wrap="wrap">
                    <Text
                      size="1"
                      color="gray"
                      data-testid={`graph-citation-relation-${index}`}
                    >
                      {relation}
                    </Text>
                    <Text size="2" weight="medium">
                      {farLabel}
                    </Text>
                    {typeof edge.weight === "number" ? (
                      <Text
                        size="1"
                        color="gray"
                        data-testid={`graph-citation-weight-${index}`}
                      >
                        {edge.weight.toFixed(2)}
                      </Text>
                    ) : null}
                  </Flex>
                  {edge.evidence ? (
                    <Text size="1" color="gray">
                      {edge.evidence}
                    </Text>
                  ) : null}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Chip label: the claim, plus the one fact worth seeing without opening it. */
export function graphCitationLabel(
  citation: TicketMessageCitation,
  fallback: string,
): string {
  if (!isGraphCitation(citation)) return fallback;
  const base = citation.graphLabel?.trim() || fallback;
  return hasConflictingVerdicts(citation) ? `${base} — verdicts differ` : base;
}
